import React, { useState, useEffect } from 'react';
import { User, X, Search, Plus, Trash2, Mail } from 'lucide-react';
import { useLocalAuth } from '../../hooks/useLocalAuth';
import { TechnicianInviteDialog } from './TechnicianInviteDialog';

interface TechnicianListProps {
  onClose: () => void;
}

interface Technician {
  id: string;
  name: string;
  company?: string;
  role?: string;
  email: string;
  lastLogin?: Date;
}

export const TechnicianList: React.FC<TechnicianListProps> = ({ onClose }) => {
  const { user } = useLocalAuth();
  const [technicians, setTechnicians] = useState<Technician[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [showInviteDialog, setShowInviteDialog] = useState(false);

  // Load technicians from localStorage
  useEffect(() => {
    loadTechnicians();
  }, []);

  const loadTechnicians = () => {
    const storedTechnicians = localStorage.getItem('installtrack_technicians');
    if (storedTechnicians) {
      setTechnicians(JSON.parse(storedTechnicians));
    } else {
      // Initialize with current user if available
      if (user) {
        const initialTechnicians = [{
          id: user.id,
          name: user.name,
          email: user.email,
          company: user.company,
          role: 'Techniker',
          lastLogin: user.lastLogin
        }];
        setTechnicians(initialTechnicians);
        localStorage.setItem('installtrack_technicians', JSON.stringify(initialTechnicians));
      }
    }
  };

  const handleInviteTechnician = (email: string) => {
    // The actual invite is handled in the dialog component
    // Here we just need to refresh the list
    loadTechnicians();
  };

  const handleDeleteTechnician = (id: string) => {
    // Don't allow deleting the current user
    if (user && id === user.id) {
      alert('Sie können Ihren eigenen Benutzer nicht löschen.');
      return;
    }

    if (window.confirm('Möchten Sie diesen Techniker wirklich löschen?')) {
      const updatedTechnicians = technicians.filter(tech => tech.id !== id);
      setTechnicians(updatedTechnicians);
      localStorage.setItem('installtrack_technicians', JSON.stringify(updatedTechnicians));
    }
  };

  const filteredTechnicians = technicians.filter(tech => 
    tech.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    tech.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (tech.company && tech.company.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold flex items-center gap-2">
            <User className="w-6 h-6 text-blue-600" />
            Techniker verwalten
          </h2>
          <button 
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="space-y-6">
          {/* Search and Add */}
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Search className="h-5 w-5 text-gray-400" />
              </div>
              <input
                type="text"
                placeholder="Techniker suchen..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full"
              />
            </div>
            
            <button
              onClick={() => setShowInviteDialog(true)}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              <Mail className="w-5 h-5" />
              <span>Techniker einladen</span>
            </button>
          </div>

          {/* Technicians list */}
          <div className="border rounded-lg overflow-hidden">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Name
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    E-Mail
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Firma
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Letzter Login
                  </th>
                  <th scope="col" className="relative px-6 py-3">
                    <span className="sr-only">Aktionen</span>
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredTechnicians.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="px-6 py-4 text-center text-gray-500">
                      Keine Techniker gefunden
                    </td>
                  </tr>
                ) : (
                  filteredTechnicians.map(technician => (
                    <tr key={technician.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="flex-shrink-0 h-10 w-10 bg-blue-100 rounded-full flex items-center justify-center">
                            <User className="h-5 w-5 text-blue-600" />
                          </div>
                          <div className="ml-4">
                            <div className="text-sm font-medium text-gray-900">
                              {technician.name}
                            </div>
                            {user && technician.id === user.id && (
                              <div className="text-xs text-green-600">
                                Aktueller Benutzer
                              </div>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {technician.email}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {technician.company || '-'}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {technician.lastLogin 
                          ? new Date(technician.lastLogin).toLocaleString('de-DE')
                          : 'Noch nicht angemeldet'
                        }
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <button
                          onClick={() => handleDeleteTechnician(technician.id)}
                          className="text-red-600 hover:text-red-900 p-1 rounded-full hover:bg-red-50"
                          title="Techniker löschen"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          <div className="flex justify-end">
            <button
              onClick={onClose}
              className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700"
            >
              Schließen
            </button>
          </div>
        </div>
      </div>

      {showInviteDialog && (
        <TechnicianInviteDialog
          onClose={() => setShowInviteDialog(false)}
          onInvite={handleInviteTechnician}
        />
      )}
    </div>
  );
};